import { useMemo } from 'react';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useUserAccountIsReady from 'src/hooks/useUserAccountIsReady';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import useTickedAllSubAccounts from 'src/hooks/useTickedAllSubAccounts';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import {
	getLowHealthWarningsForAccount,
	type LowHealthWarning,
} from 'src/utils/lowHealthWarnings';

export type { LowHealthWarning };

/**
 * Returns all low-health warnings (cross and isolated positions) below the threshold.
 * @param thresholdPercent - Health percentage threshold (default 20)
 * @param allSubaccounts - If true, checks all subaccounts; if false, checks only current user
 */
export function useLowHealthWarnings(
	thresholdPercent = 20,
	allSubaccounts = false
): LowHealthWarning[] {
	const velocityClient = useVelocityClient();
	const velocityClientIsReady = useVelocityClientIsReady();
	const isUserAccountReady = useUserAccountIsReady();
	const currentUserKey = useVelocityAccountStore((s) => s.currentUserKey);
	const isConnected = useWalletIsConnected();
	const allAccounts = useTickedAllSubAccounts();

	const currentUserClient =
		velocityClientIsReady &&
		isUserAccountReady &&
		velocityClient?.hasUser() &&
		velocityClient.getUser()?.isSubscribed
			? velocityClient.getUser()
			: undefined;

	return useMemo(() => {
		const warnings: LowHealthWarning[] = [];

		if (allSubaccounts) {
			if (!isConnected || allAccounts.length === 0) return warnings;

			for (const acct of allAccounts) {
				if (!acct.client?.isSubscribed) continue;
				warnings.push(
					...getLowHealthWarningsForAccount(
						acct.client,
						acct.userKey,
						thresholdPercent
					)
				);
			}
		} else {
			if (!currentUserClient) return warnings;

			warnings.push(
				...getLowHealthWarningsForAccount(
					currentUserClient,
					currentUserKey ?? '',
					thresholdPercent
				)
			);
		}

		return warnings;
	}, [
		allSubaccounts,
		isConnected,
		allAccounts,
		currentUserClient,
		currentUserClient?.isSubscribed,
		currentUserKey,
		thresholdPercent,
	]);
}

// Legacy interface for backward compatibility
export interface IsolatedLowHealthWarning {
	marketIndex: number;
	health: number;
	message: string;
}

/**
 * Returns the worst (lowest health) isolated position warning for the current user.
 * This is a convenience wrapper for backward compatibility with the old API.
 */
export function useIsolatedLowHealthWarning(
	thresholdPercent = 20
): IsolatedLowHealthWarning | undefined {
	const warnings = useLowHealthWarnings(thresholdPercent, false);

	return useMemo(() => {
		const isolatedWarnings = warnings.filter((w) => w.type === 'isolated');
		if (isolatedWarnings.length === 0) return undefined;

		// Find the worst (lowest health)
		const worst = isolatedWarnings.reduce((prev, curr) =>
			curr.health < prev.health ? curr : prev
		);

		return {
			marketIndex: worst.marketIndex!,
			health: worst.health,
			message: `${worst.marketLabel} (Isolated) Health is below ${thresholdPercent}%`,
		};
	}, [warnings, thresholdPercent]);
}
